import type {
  CommunicationConsent,
  CommunicationConsentAction,
  CommunicationConsentPurpose,
  ContactPreferences,
  Id,
  Profile,
} from '../models';
import { newId, nowIso } from './ids';
import { getDb, updateDb } from './store';

export const SMS_OPERATIONAL_DISCLOSURE_VERSION = 'sms-operational-v1';
export const SMS_MARKETING_DISCLOSURE_VERSION = 'sms-marketing-v1';
export const SMS_OPT_OUT_DISCLOSURE_VERSION = 'sms-opt-out-v1';

export type SmsConsentState =
  | 'no_phone'
  | 'invalid_phone'
  | 'not_opted_in'
  | 'opted_in'
  | 'opted_out'
  | 'phone_changed';

export interface SmsPurposeConsentStatus {
  purpose: CommunicationConsentPurpose;
  state: SmsConsentState;
  message: string;
  consent?: CommunicationConsent;
  previousConsent?: CommunicationConsent;
}

export interface SmsConsentStatus {
  phone: string;
  normalizedPhone: string;
  phoneValid: boolean;
  operational: SmsPurposeConsentStatus;
  marketing: SmsPurposeConsentStatus;
}

export interface RecordSmsConsentInput {
  profileId: Id;
  registrationId?: Id;
  tournamentId?: Id;
  phone: string;
  purpose: CommunicationConsentPurpose;
  source?: string;
  senderName?: string;
  ipAddress?: string;
  userAgent?: string;
}

export interface RecordSmsOptOutInput {
  profileId: Id;
  purpose: CommunicationConsentPurpose;
  phone?: string;
  source?: string;
  ipAddress?: string;
  userAgent?: string;
}

export function recordSmsConsent(input: RecordSmsConsentInput): CommunicationConsent {
  const disclosure = smsDisclosure(input.purpose, input.senderName);
  return recordConsent({
    profileId: input.profileId,
    registrationId: input.registrationId,
    tournamentId: input.tournamentId,
    phone: input.phone,
    purpose: input.purpose,
    action: 'opt_in',
    source: input.source ?? 'registration',
    disclosureVersion: disclosure.version,
    disclosureText: disclosure.text,
    ipAddress: input.ipAddress,
    userAgent: input.userAgent,
  });
}

export function recordSmsOptOut(input: RecordSmsOptOutInput): CommunicationConsent {
  const profile = getDb().profiles.find((person) => person.id === input.profileId);
  return recordConsent({
    profileId: input.profileId,
    phone: input.phone ?? profile?.phone ?? '',
    purpose: input.purpose,
    action: 'opt_out',
    source: input.source ?? 'admin_opt_out',
    disclosureVersion: SMS_OPT_OUT_DISCLOSURE_VERSION,
    disclosureText: 'Admin recorded an SMS opt-out or revocation request on behalf of this Directory profile.',
    ipAddress: input.ipAddress,
    userAgent: input.userAgent,
  });
}

export function getSmsConsentStatus(
  profile: Profile,
  consents: CommunicationConsent[] = getDb().communicationConsents,
): SmsConsentStatus {
  const phone = profile.phone ?? '';
  const normalizedPhone = normalizeSmsPhone(phone);
  const phoneValid = isValidSmsPhone(phone);
  return {
    phone,
    normalizedPhone,
    phoneValid,
    operational: getPurposeStatus(profile.id, normalizedPhone, phoneValid, 'operational', consents),
    marketing: getPurposeStatus(profile.id, normalizedPhone, phoneValid, 'marketing', consents),
  };
}

export function deriveContactPreferences(
  profile: Profile,
  consents: CommunicationConsent[] = getDb().communicationConsents,
): ContactPreferences {
  const status = getSmsConsentStatus(profile, consents);
  return {
    ...(profile.contactPreferences ?? {}),
    smsOptIn: status.operational.state === 'opted_in',
    smsMarketingOptIn: status.marketing.state === 'opted_in',
    smsOptOut: status.operational.state === 'opted_out' && status.marketing.state !== 'opted_in',
  };
}

export function smsDisclosure(
  purpose: CommunicationConsentPurpose,
  senderName = 'The Scorekeeper',
): { version: string; text: string } {
  const sender = senderName.trim() || 'The Scorekeeper';
  if (purpose === 'marketing') {
    return {
      version: SMS_MARKETING_DISCLOSURE_VERSION,
      text: `By checking this box, you agree to receive marketing and promotional text messages from ${sender} at the phone number provided. Message and data rates may apply. Reply STOP to opt out and HELP for help. Consent is not required to register or buy anything.`,
    };
  }
  return {
    version: SMS_OPERATIONAL_DISCLOSURE_VERSION,
    text: `By checking this box, you agree to receive event text messages from ${sender} at the phone number provided. Message and data rates may apply. Reply STOP to opt out and HELP for help. Consent is not required to register or buy anything.`,
  };
}

export function normalizeSmsPhone(value?: string | null): string {
  return value?.replace(/\D+/g, '') ?? '';
}

export function isValidSmsPhone(value?: string | null): boolean {
  const digits = normalizeSmsPhone(value);
  return digits.length >= 10 && digits.length <= 15;
}

function recordConsent(input: {
  profileId: Id;
  registrationId?: Id;
  tournamentId?: Id;
  phone: string;
  purpose: CommunicationConsentPurpose;
  action: CommunicationConsentAction;
  source: string;
  disclosureVersion: string;
  disclosureText: string;
  ipAddress?: string;
  userAgent?: string;
}): CommunicationConsent {
  const ts = nowIso();
  const phone = normalizeSmsPhone(input.phone);
  const consent: CommunicationConsent = {
    id: newId('consent'),
    kind: 'communication-consent',
    createdAt: ts,
    updatedAt: ts,
    profileId: input.profileId,
    registrationId: input.registrationId,
    tournamentId: input.tournamentId,
    phone,
    channel: 'sms',
    purpose: input.purpose,
    action: input.action,
    source: input.source,
    disclosureVersion: input.disclosureVersion,
    disclosureText: input.disclosureText,
    capturedAt: ts,
    ipAddress: input.ipAddress,
    userAgent: input.userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : undefined),
  };

  updateDb((db) => {
    const nextConsents = [...db.communicationConsents, consent];
    return {
      ...db,
      communicationConsents: nextConsents,
      profiles: db.profiles.map((profile) => {
        if (profile.id !== input.profileId) return profile;
        return {
          ...profile,
          contactPreferences: deriveContactPreferences(profile, nextConsents),
          updatedAt: ts,
        };
      }),
    };
  });

  return consent;
}

function getPurposeStatus(
  profileId: Id,
  normalizedPhone: string,
  phoneValid: boolean,
  purpose: CommunicationConsentPurpose,
  consents: CommunicationConsent[],
): SmsPurposeConsentStatus {
  if (!normalizedPhone) {
    return { purpose, state: 'no_phone', message: 'Add a valid phone number before text consent can be recorded.' };
  }
  if (!phoneValid) {
    return { purpose, state: 'invalid_phone', message: 'Enter a valid phone number before text consent can be recorded.' };
  }

  const rows = consents
    .map((consent, index) => ({ consent, index }))
    .filter(({ consent }) => consent.profileId === profileId && consent.channel === 'sms' && consent.purpose === purpose)
    .sort((a, b) => {
      const byCapturedAt = (b.consent.capturedAt || b.consent.createdAt).localeCompare(
        a.consent.capturedAt || a.consent.createdAt,
      );
      return byCapturedAt || b.index - a.index;
    })
    .map(({ consent }) => consent);
  const matchingPhone = rows.find((consent) => normalizeSmsPhone(consent.phone) === normalizedPhone);
  if (matchingPhone) {
    if (matchingPhone.action === 'opt_out') {
      return {
        purpose,
        state: 'opted_out',
        message: `${purposeLabel(purpose)} text consent was opted out.`,
        consent: matchingPhone,
      };
    }
    return {
      purpose,
      state: 'opted_in',
      message: `${purposeLabel(purpose)} text consent is recorded for this phone number.`,
      consent: matchingPhone,
    };
  }

  if (rows.length > 0) {
    return {
      purpose,
      state: 'phone_changed',
      message: 'Phone number changed since text consent was collected. New consent must be captured during registration.',
      previousConsent: rows[0],
    };
  }

  return {
    purpose,
    state: 'not_opted_in',
    message: 'Not opted in. Text consent is collected during registration.',
  };
}

function purposeLabel(purpose: CommunicationConsentPurpose): string {
  return purpose === 'marketing' ? 'Marketing' : 'Event';
}
